import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, refreshSession, setAccessToken, setSessionExpiredHandler } from '../api/client';

export type UserRole = 'ADMIN' | 'ACCOUNTANT' | 'MANAGER' | 'VIEWER';

export interface CurrentUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
}

interface LoginResponse {
  accessToken: string;
  user: CurrentUser;
}

interface AuthContextValue {
  user: CurrentUser | null;
  /** true, пока идёт восстановление сессии при загрузке страницы */
  initializing: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (...roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  /**
   * При загрузке страницы access-токена в памяти нет — он там не переживает
   * перезагрузку. Но refresh-токен лежит в cookie, поэтому пробуем поднять
   * сессию молча: если получилось, пользователь не увидит экран входа.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const restored = await refreshSession();
      if (cancelled) return;

      if (restored) {
        try {
          const data = await api.get<{ user: CurrentUser }>('/auth/me');
          if (!cancelled) setUser(data.user);
        } catch {
          if (!cancelled) setUser(null);
        }
      }
      if (!cancelled) setInitializing(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Если сессия протухла посреди работы, клиент сообщит об этом сюда
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      setAccessToken(null);
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api.post<LoginResponse>('/auth/login', { email, password });
    setAccessToken(data.accessToken);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      // Чистим состояние даже если запрос не прошёл — иначе пользователь
      // останется в интерфейсе с нерабочей сессией
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const hasRole = useCallback(
    (...roles: UserRole[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, initializing, login, logout, hasRole }),
    [user, initializing, login, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен вызываться внутри AuthProvider');
  }
  return context;
}

/** Человекочитаемые названия ролей для интерфейса. */
export const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: 'Администратор',
  ACCOUNTANT: 'Бухгалтер',
  MANAGER: 'Менеджер',
  VIEWER: 'Просмотр',
};
